import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, User, Check, X } from 'lucide-react';
import { useParadigm } from '../../contexts/ParadigmContext';

/**
 * UserPicker - A searchable, multi-select user picker component
 *
 * Props:
 * - users: Array of user objects with at least { id, email, name }
 * - selectedIds: Array of selected user IDs
 * - onChange: Callback when selection changes (receives array of IDs)
 * - placeholder: Placeholder text for search
 * - disabled: Disable the picker
 * - loading: Show loading state
 * - maxHeight: Max height of the list (default 240px)
 * - className: Additional CSS classes
 * - label: Label text
 * - showRole: Show user role
 * - showOrganization: Show user organization
 * - excludeIds: Array of user IDs to exclude from the list
 */
const UserPicker = ({
  users = [],
  selectedIds = [],
  onChange,
  placeholder = 'Search users...',
  disabled = false,
  loading = false,
  maxHeight = 240,
  className = '',
  label = '',
  showRole = true,
  showOrganization = false,
  excludeIds = [],
}) => {
  const { getRoleLabel } = useParadigm();
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);

  // Filter users based on search term and exclusions
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Exclude specified users
      if (excludeIds.includes(user.id)) return false;

      // Filter by search term
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower)
      );
    });
  }, [users, searchTerm, excludeIds]);

  // Separate selected and unselected users
  const selectedUsers = filteredUsers.filter(u => selectedIds.includes(u.id));
  const unselectedUsers = filteredUsers.filter(u => !selectedIds.includes(u.id));

  // Combined sorted list (selected first)
  const sortedUsers = [...selectedUsers, ...unselectedUsers];

  const handleToggleUser = (userId) => {
    if (disabled) return;

    const newSelected = selectedIds.includes(userId)
      ? selectedIds.filter(id => id !== userId)
      : [...selectedIds, userId];

    onChange(newSelected);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredUsers.map(u => u.id);
    const newSelected = [...new Set([...selectedIds, ...allFilteredIds])];
    onChange(newSelected);
  };

  const handleDeselectAll = () => {
    const filteredIds = new Set(filteredUsers.map(u => u.id));
    const newSelected = selectedIds.filter(id => !filteredIds.has(id));
    onChange(newSelected);
  };

  const selectedCount = selectedIds.length;
  const visibleSelectedCount = filteredUsers.filter(u => selectedIds.includes(u.id)).length;

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="border border-gray-300 rounded-lg overflow-hidden">
        {/* Search and Controls Header */}
        <div className="p-2 bg-gray-50 border-b border-gray-200 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">
              {visibleSelectedCount} of {filteredUsers.length} selected
              {selectedCount !== visibleSelectedCount && ` (${selectedCount} total)`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disabled || filteredUsers.length === 0}
                className="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleDeselectAll}
                disabled={disabled || visibleSelectedCount === 0}
                className="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* User List */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: `${maxHeight}px` }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-500">Loading users...</span>
            </div>
          ) : sortedUsers.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {searchTerm ? 'No users match your search' : 'No users available'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedUsers.map(user => {
                const isSelected = selectedIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleToggleUser(user.id)}
                    disabled={disabled}
                    className={`w-full px-3 py-2 flex items-center gap-3 text-left transition-colors ${
                      disabled
                        ? 'cursor-not-allowed opacity-60'
                        : 'hover:bg-gray-50 cursor-pointer'
                    } ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* User Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {user.name || user.email}
                        </span>
                        {isSelected && (
                          <span className="text-xs text-blue-600 font-medium">
                            Selected
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="truncate">{user.email}</span>
                        {showRole && user.role && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{getRoleLabel(user.role)}</span>
                          </>
                        )}
                        {showOrganization && user.organization_name && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="truncate">{user.organization_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selected Count Footer */}
      {selectedCount > 0 && (
        <div className="mt-2 text-sm text-gray-600">
          {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};

export default UserPicker;
